import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import CommentsModal from "Components/Generic/Comment/Modal";
import { useAppState } from "Context/StateContext";
import { Comment } from "Models/comment";
import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask, DespatchTaskComment } from "Models/despatch.task";
import { Task } from "Models/task";
import { TaskComment } from "Models/task.comment";
import { User } from "Models/user";
import { useTranslation } from "react-i18next";
import { uniqueArrayWithComparator } from "Util/uniqueArray";
import { operationsPermissions, hasPermission, checkResourceCan, FN_NAMES } from "Util/permissions";
import { ResourceSubGroup } from "Util/enums";
import { ResourceName, ResourceCanEnum } from "Util/enums/accessRule";

const parseTaskCommentToDespatchTaskComment = (
  taskComment: TaskComment,
  currentUser?: User
): DespatchTaskComment => ({
  id: taskComment.id!,
  createdAt: taskComment.createdAt!,
  message: taskComment.message,
  user: {
    fullName: taskComment?.user?.fullName || currentUser?.fullName || "",
    id: taskComment?.userId || currentUser?.id!,
    picture: taskComment?.user?.picture || currentUser?.picture!,
  },
  userId: taskComment.userId || currentUser?.id!,
});

interface Props {
  onSuccessCreate: (
    comment: Pick<TaskComment, "id" | "message" | "createdAt" | "updatedAt" | "userId" | "taskId">,
    task: DespatchTask | Task
  ) => void;
  onSuccessDelete: (commentId: TaskComment["id"], task: DespatchTask | Task) => void;
}

export interface DespatchTaskCommentsModalHandles {
  open: (task: DespatchTask | Task) => void;
}

const DespatchTaskCommentsModalComponent: ForwardRefRenderFunction<
  DespatchTaskCommentsModalHandles,
  Props
> = ({ onSuccessCreate, onSuccessDelete }, ref) => {
  const { t: translate } = useTranslation();
  const { currentUser, userPermissions } = useAppState();
  const isUserEnableToEdit = operationsPermissions({
    subGroup: ResourceSubGroup.DISPATCH,
    resourceName: ResourceName.DISPATCH,
    currentUser,
  })?.edit;

  const { useCreateTaskComment, useDeleteTaskComment } = useDespatchActions();
  const { mutate: createTaskComment, isLoading: isLoadingTaskComment } = useCreateTaskComment(
    (response, variables) => onSuccessCreate(response, variables.task)
  );

  const { mutate: deleteTaskComment } = useDeleteTaskComment((response, variables) =>
    onSuccessDelete(variables.commentId, variables.task)
  );

  const [task, setTask] = useState<DespatchTask | Task | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpenModal = (_task: DespatchTask | Task, _crew?: DespatchCrew) => {
    setTask(_task);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpenModal,
  }));

  const onSubmit = (data: Comment) => {
    if (!task || !data.message) return;

    createTaskComment(
      {
        data: {
          message: data.message,
          userId: currentUser?.id!,
          taskId: task.id!,
        },
        task,
      },
      {
        onSuccess: (response) => {
          const newTask = parseTaskCommentToDespatchTaskComment(response.data, currentUser);

          setTask((prev) => {
            if (!prev) return prev;

            const newValue = uniqueArrayWithComparator(
              [...(prev.comments || []), newTask],
              (a, b) => a.id === b.id
            ) as any[];

            return {
              ...prev,
              comments: newValue,
            };
          });
        },
      }
    );
  };

  const onDeleteTaskComment = (data: Comment) => {
    if (!task || !data?.id) return;

    deleteTaskComment({
      commentId: data.id,
      task,
    });

    setTask((prev) => {
      if (!prev) prev = {} as typeof task;
      const lastComments = [...(prev.comments as any[])];

      prev.comments = lastComments.filter((comment: any) => comment.id !== data.id);

      return prev;
    });
  };

  const commentsParsed: Comment[] = useMemo(() => {
    const comments = (task?.comments || []).map(
      (data) =>
        ({
          id: data.id,
          createdAt: data.createdAt,
          identifier: "task",
          message: data.message,
          userId: data.userId,
          user: data.user,
        } as Comment)
    );

    return comments.reverse();
  }, [task?.comments]);

  const hasPermissionToCreateAndEdit =
    isUserEnableToEdit &&
    checkResourceCan(userPermissions!, ResourceName.DISPATCH, ResourceCanEnum.EDIT) &&
    hasPermission(userPermissions!, FN_NAMES.FUNCTION_OPERATIONS_DISPATCH_NEW_COMMENT);

  return (
    <CommentsModal
      comments={commentsParsed}
      count={commentsParsed.length}
      hasMore={false}
      onLoadMore={() => {}}
      isLoadingCreate={isLoadingTaskComment}
      isLoadingHistory={true}
      onClose={handleCloseModal}
      onSubmit={onSubmit}
      maxCommentLength={255}
      open={open}
      title={translate("comments")}
      placeholderText={
        hasPermissionToCreateAndEdit
          ? `${translate("writeAComment")} ${translate("in")} '${
              task?.job?.reference ?? task?.name
            }'`
          : ""
      }
      onDeleteComment={onDeleteTaskComment}
      hasPermission={hasPermissionToCreateAndEdit}
    />
  );
};

export const DespatchTaskCommentsModal = forwardRef(DespatchTaskCommentsModalComponent);
